import { FC, useState, useEffect, useContext, useRef, useCallback } from "react";
import Cookies from "universal-cookie";
import ConstantsContext from "../../../../store/Constants-context";
import { getPeopleV2 } from "../../../../hooks/people.controller";
import { nameGenerator } from "../../../../hooks/utils";
import "./menuinner.css";

interface result {
  key: string;
  id: string;
  name: string;
  label: string;
  link: string;
}

const DEBOUNCE_MS = 1000;
const MIN_CHARS = 3;
const RESULT_LIMIT = 500;

const cookies = new Cookies();

const typeLabel = (personType: string, memberName: string): string => {
  switch (personType) {
    case "teacher": return "Staff";
    case "visitor": return "Visitor";
    case "student": return memberName || "Student";
    case "guardian": return "Guardian";
    default: return personType || "";
  }
};

const urlType = (personType: string): string => {
  if (personType === "teacher") return "staff";
  return personType;
};

const MenuInner: FC = () => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<result[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const constantsCtx = useContext(ConstantsContext);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentReqId = useRef(0);

  const runSearch = useCallback(async (query: string) => {
    const reqId = ++currentReqId.current;

    const userMenu: string[] = cookies.get("user_menu_config")
      ? String(cookies.get("user_menu_config")).split(",")
      : [];
    const personTypes = ["student", "visitor"];
    if (userMenu.includes("setup")) personTypes.push("teacher");

    const filters: Record<string, string | number> = {
      subString: query.toLowerCase(),
      return_only: "people_id,first_name,last_name,person_type,site_id",
      limit: RESULT_LIMIT,
    };

    setIsLoading(true);
    try {
      const response: any = await getPeopleV2(filters);
      if (reqId !== currentReqId.current) return;
      const items: any[] = Array.isArray(response)
        ? response
        : (response && response.Items) || [];

      // The backend `subString` filter also matches against email and
      // other non-name fields, so enforce a name-only match on the
      // client to avoid surfacing irrelevant results.
      const needle = query.toLowerCase();
      const filtered = items.filter((p) => {
        const first = (p.first_name || "").toLowerCase();
        const last = (p.last_name || "").toLowerCase();
        const full = (first + " " + last).trim();
        return (
          first.indexOf(needle) !== -1 ||
          last.indexOf(needle) !== -1 ||
          full.indexOf(needle) !== -1
        );
      });

      // Collapse duplicates by name + person_type. Clicking the row
      // navigates using the first site_id seen for that group.
      const groups: Record<string, result> = {};
      const order: string[] = [];
      filtered.forEach((p) => {
        const displayName = nameGenerator(p.first_name, p.last_name, p.people_id);
        const key = (p.person_type || "") + "|" + displayName.toLowerCase();
        if (!groups[key]) {
          const type = urlType(p.person_type);
          groups[key] = {
            key,
            id: p.people_id,
            name: displayName,
            label: typeLabel(p.person_type, constantsCtx.MEMBER_NAME),
            link: `edit_user.html?type=${encodeURIComponent(type)}&id=${encodeURIComponent(p.people_id)}&siteId=${encodeURIComponent(p.site_id)}`,
          };
          order.push(key);
        }
      });

      setSearchResults(order.map((k) => groups[k]));
    } catch (err) {
      console.log("Global search error:", err);
    } finally {
      if (reqId === currentReqId.current) setIsLoading(false);
    }
  }, [constantsCtx.MEMBER_NAME]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const val = search.trim();
    if (val.length < MIN_CHARS) {
      setSearchResults([]);
      setHasSearched(false);
      setIsLoading(false);
      return;
    }
    debounceTimer.current = setTimeout(() => {
      setHasSearched(true);
      runSearch(val);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search, runSearch]);

  const showDropdown =
    search.trim().length >= MIN_CHARS && (isLoading || hasSearched);

  return (
    <>
      <div className="container-fluid" style={{ width: "300px" }}>
        <div className="row">
          <div className="col-lg-12 fv-row">
            <div className="input-group mt-5 mb-1 pb-1 searchPersonWrapper">
              <input
                type="text"
                className="form-control searchPerson"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setSearch("");
                    setSearchResults([]);
                  }
                }}
                placeholder="Search Person"
              />
              <div className="input-group-append">
                <button className="input-group-text" type="button">
                  {isLoading ? (
                    <span className="gs-input-spinner" />
                  ) : (
                    <i className="bi bi-search fs-7"></i>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {showDropdown && (
          <div className="searchResults">
            <ul className="p-0 m-0">
              {searchResults.length === 0 && !isLoading && (
                <li className="p-3" style={{ color: "#888", cursor: "default" }}>
                  No matches
                </li>
              )}
              {searchResults.map((r) => (
                <li
                  key={r.key}
                  className="p-3"
                  style={{ color: "#26a69a", whiteSpace: "nowrap" }}
                  onClick={() => (window.location.href = r.link)}
                >
                  <span style={{ display: "inline" }}>{r.name}</span>{" "}
                  <span style={{ display: "inline", color: "#777" }}>
                    ({r.label})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default MenuInner;
